import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ShoppingCart, Store, X, Zap } from "lucide-react";
import { Dialog, DialogPanel } from "@tremor/react";
import HarViewer from "../HarViewer/index.jsx";
import FindingCard from "./FindingCard.jsx";
import client from "../../api/client.js";

const FLOW_ICONS = {
  homepage: Store,
  product_page: Zap,
  add_to_cart: ShoppingCart,
  checkout_handoff: AlertTriangle,
};

const STATUS_STYLES = {
  up: "bg-green-100 text-green-700 border-green-200",
  degraded: "bg-amber-100 text-amber-700 border-amber-200",
  down: "bg-red-100 text-red-700 border-red-200",
};

function StatusBadge({ status }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600 border-gray-200",
      ].join(" ")}
    >
      {status ?? "unknown"}
    </span>
  );
}

function FlowCard({ phase, result, flowLabels }) {
  const Icon = FLOW_ICONS[phase] ?? Store;
  const { t } = useTranslation("monitor_detail");
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-gray-100 p-2 text-gray-500">
            <Icon className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {flowLabels[phase] ?? phase}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              {result?.message ?? t("shopify_drawer.no_data")}
            </p>
          </div>
        </div>
        <StatusBadge status={result?.status} />
      </div>
      {result?.duration_ms != null && (
        <p className="mt-3 text-xs text-gray-400">
          {Math.round(result.duration_ms)} ms
        </p>
      )}
    </div>
  );
}

export default function ShopifyCheckDrawer({ check, onClose }) {
  const { t } = useTranslation("monitor_detail");
  const meta = check.data ?? {};
  const flow = meta.flow ?? {};
  const journey = meta.journey ?? [];
  const screenshots = meta.screenshots ?? [];
  const findings = meta.findings ?? [];
  const assetSummary = meta.asset_summary ?? null;
  const [har, setHar] = useState(null);
  const [harLoading, setHarLoading] = useState(false);
  const [harError, setHarError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [activeScreenshot, setActiveScreenshot] = useState(0);

  const FLOW_LABELS = {
    homepage: t("shopify_drawer.flow_homepage"),
    product_page: t("shopify_drawer.flow_product_page"),
    add_to_cart: t("shopify_drawer.flow_add_to_cart"),
    checkout_handoff: t("shopify_drawer.flow_checkout_handoff"),
  };

  const brokenFindings = useMemo(
    () => findings.filter((f) => f.severity === "broken"),
    [findings]
  );
  const noticeFindings = useMemo(
    () => findings.filter((f) => f.severity === "notice"),
    [findings]
  );

  useEffect(() => {
    if (!meta.har_url) return;
    setHarLoading(true);
    client
      .get(meta.har_url)
      .then((res) => {
        const data = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
        setHar(data);
      })
      .catch(() => setHarError(t("shopify_drawer.error_har")))
      .finally(() => setHarLoading(false));
  }, [meta.har_url, t]);

  useEffect(() => {
    setActiveScreenshot(0);
  }, [check.time, screenshots.length]);

  const tabs = [
    { id: "overview", label: t("shopify_drawer.tab_overview") },
    {
      id: "journey",
      label: t("shopify_drawer.tab_journey", { count: journey.length }),
    },
    findings.length > 0 && {
      id: "findings",
      label: t("shopify_drawer.tab_findings", { count: findings.length }),
    },
    screenshots.length > 0 && {
      id: "screenshots",
      label: t("shopify_drawer.tab_screenshots", { count: screenshots.length }),
    },
    meta.har_url && { id: "har", label: t("shopify_drawer.tab_har") },
  ].filter(Boolean);

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-4xl flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {t("shopify_drawer.title")}
            </h2>
            <p className="mt-0.5 text-xs text-gray-400">
              {new Date(check.time).toLocaleString()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-gray-100 px-6 py-4 shrink-0">
          <div className="flex flex-wrap items-start gap-3">
            <StatusBadge status={check.status} />
            <div>
              <p className="text-sm font-medium text-gray-900">
                {meta.summary ?? check.error ?? t("shopify_drawer.no_summary")}
              </p>
              {meta.store_url && (
                <p className="mt-1 text-xs text-gray-400">{meta.store_url}</p>
              )}
            </div>
          </div>
          {brokenFindings.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {brokenFindings.map((f) => (
                <span
                  key={f.id}
                  className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700"
                >
                  {f.title}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-1 border-b border-gray-200 px-6 shrink-0">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={[
                "-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                tab === item.id
                  ? "border-teal-600 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700",
              ].join(" ")}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {Object.keys(FLOW_LABELS).map((phase) => (
                  <FlowCard
                    key={phase}
                    phase={phase}
                    result={flow[phase]}
                    flowLabels={FLOW_LABELS}
                  />
                ))}
              </div>

              {meta.summary && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("shopify_drawer.summary_title")}
                  </p>
                  <p className="mt-2 text-sm text-gray-700">{meta.summary}</p>
                </div>
              )}

              {assetSummary?.total_bytes > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("shopify_drawer.page_weight_title")}
                  </p>
                  <p className="mt-2 text-sm font-medium text-gray-900">
                    {t("shopify_drawer.page_weight_value", {
                      kb: (assetSummary.total_bytes / 1024).toFixed(0),
                    })}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                    {Object.entries(assetSummary.by_type ?? {})
                      .filter(([, v]) => v > 0)
                      .sort(([, a], [, b]) => b - a)
                      .map(([type, bytes]) => (
                        <span key={type}>
                          <span className="font-medium capitalize">{type}</span>{" "}
                          {(bytes / 1024).toFixed(0)} KB
                        </span>
                      ))}
                  </div>
                  {assetSummary.top_resources?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs font-medium text-gray-500">
                        {t("shopify_drawer.largest_resources")}
                      </p>
                      {assetSummary.top_resources.map((r, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between gap-2 text-xs"
                        >
                          <span className="truncate font-mono text-gray-400">
                            {r.url.replace(/^https?:\/\/[^/]+/, "")}
                          </span>
                          <span className="shrink-0 text-gray-600">
                            {(r.size_bytes / 1024).toFixed(0)} KB
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "findings" && (
            <div className="space-y-6">
              {brokenFindings.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-600">
                    {t("shopify_drawer.findings_broken_heading", {
                      count: brokenFindings.length,
                    })}
                  </p>
                  {brokenFindings.map((f) => (
                    <FindingCard key={f.id} finding={f} />
                  ))}
                </div>
              )}

              {noticeFindings.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                    {t("shopify_drawer.findings_notice_heading", {
                      count: noticeFindings.length,
                    })}
                  </p>
                  {noticeFindings.map((f) => (
                    <FindingCard key={f.id} finding={f} />
                  ))}
                </div>
              )}

              {findings.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
                  <p className="text-sm font-medium text-gray-600">
                    {t("shopify_drawer.findings_none")}
                  </p>
                </div>
              )}
            </div>
          )}

          {tab === "journey" && (
            <div className="space-y-3">
              {journey.map((item, index) => (
                <div
                  key={`${item.phase}-${index}`}
                  className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {FLOW_LABELS[item.phase] ?? item.phase}
                      </p>
                      <p className="mt-1 text-sm text-gray-600">{item.message}</p>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-400">
                    {item.duration_ms != null && (
                      <span>{Math.round(item.duration_ms)} ms</span>
                    )}
                    {item.url && <span className="truncate">{item.url}</span>}
                    {item.selector && (
                      <span className="font-mono">{item.selector}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "screenshots" && screenshots.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {screenshots.map((shot, index) => (
                  <button
                    key={`${shot.phase}-${index}`}
                    type="button"
                    onClick={() => setActiveScreenshot(index)}
                    className={[
                      "rounded-full border px-3 py-1 text-xs font-medium",
                      activeScreenshot === index
                        ? "border-teal-600 bg-teal-50 text-teal-600"
                        : "border-gray-200 bg-white text-gray-500",
                    ].join(" ")}
                  >
                    {FLOW_LABELS[shot.phase] ?? shot.phase}
                  </button>
                ))}
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3">
                  <p className="text-sm font-semibold text-gray-900">
                    {FLOW_LABELS[screenshots[activeScreenshot]?.phase] ??
                      screenshots[activeScreenshot]?.phase}
                  </p>
                  {screenshots[activeScreenshot]?.note && (
                    <p className="mt-1 text-xs text-gray-400">
                      {screenshots[activeScreenshot].note}
                    </p>
                  )}
                </div>
                {screenshots[activeScreenshot]?.url && (
                  <img
                    src={screenshots[activeScreenshot].url}
                    alt={t("shopify_drawer.img_alt", {
                      phase: screenshots[activeScreenshot].phase,
                    })}
                    className="w-full rounded border border-gray-200"
                  />
                )}
              </div>
            </div>
          )}

          {tab === "har" && meta.har_url && (
            <>
              {harLoading && (
                <p className="text-xs text-gray-400">
                  {t("shopify_drawer.loading_har")}
                </p>
              )}
              {harError && <p className="text-xs text-red-500">{harError}</p>}
              {har && <HarViewer har={har} />}
            </>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}
